// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// The alarm's operator actions — one card, derived from AEM's state machine.
//
// This used to hardcode a four-step ladder (unacknowledged -> acknowledged ->
// work order raised -> work order completed) because only `acked` was real and
// the work-order steps lived in component state. AEM's event-driven machine
// makes all of them durable states, so the ladder is gone: the card asks the
// delta which events the alarm's CURRENT state actually accepts and renders
// those. See types/alarmFsm.ts.
//
// Why derive rather than list: the operator cells upstream are deliberately
// partial, and an undefined cell answers 409. A hardcoded button set would
// therefore offer actions that cannot succeed. Deriving makes that class of
// error unrepresentable.

import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Button } from '@carbon/react';
import { Checkmark, Notification } from '@carbon/icons-react';
import type { Alarm } from '../../types/alarm';
import AlarmOwnerRow from './AlarmOwnerRow';
import { useFlagOn } from '../../hooks/useFlagOn';
import type { WorkOrder } from '../../types/workOrder';
import {
  effectiveEvents,
  isAckedState,
  isClearedState,
  isKnownState,
  shownStatusLabel,
} from '../../types/alarmFsm';
import type { AlarmMachine, OperatorEvent } from '../../types/alarmFsm';
import {
  alarmEventErrorMessage,
  isAlarmEventRetryable,
  useApplyAlarmEvent,
} from '../../hooks/useAlarmMutation';
import { useWorkOrderLookup } from '../../hooks/useWorkOrders';
import { alarmLinkLabel, workOrderDraftFromAlarm } from '../../services/alarmWorkOrderDraft';
import { alarmDetailPath } from '../../services/alarmSelectors';
import { useAlarmWorkOrderStore } from '../../stores/alarmWorkOrderStore';
import { useRaisedWorkOrder, usePendingWorkOrderEvent } from '../../hooks/useAlarmWorkOrderLinks';
import { useIsMounted } from '../../hooks/useIsMounted';
import WorkOrderCreateModal from './WorkOrderCreateModal';

interface Props {
  alarm: Alarm;
  siteId: string;
  /** Alarm re-fetch. apply-event returns 204 with no body, so re-reading the
      alarm is the only way to learn the state the machine resolved to. When it
      returns a promise, the card holds its pending state until that settles. */
  onEventApplied: () => void | Promise<unknown>;
  /** Which machine governs this row. Alarms are open_loop; see alarmFsm.ts. */
  machine?: AlarmMachine;
  /** The row is an anomaly (an AEM finding). Only Acknowledge is offered: Create
      work order stays off until product decides how findings meet work orders
      (ADR-0039: a finding is acked or closed, "Promote to alarm" is the gate into
      the alarm lifecycle), and Close is the sibling card's "Not an alarm". */
  finding?: boolean;
}

/** The alarm on a re-read result, when the caller's refetch hands it back. */
function alarmOf(result: unknown): { status?: string | null } | undefined {
  if (!result || typeof result !== 'object' || !('data' in result)) return undefined;
  const data = (result as { data?: unknown }).data;
  return data && typeof data === 'object' ? (data as { status?: string | null }) : undefined;
}

/** What another card can ask of this one. */
export interface AlarmNextStepCardHandle {
  /** Open the Create work order dialog, as the card's own button does. */
  raiseWorkOrder: () => void;
}

/**
 * Why "Create work order" is blocked, or null when the card offers it: the
 * machine must allow it, the alarm must be acknowledged (the machine lets an
 * unacknowledged alarm go straight there, but the operator flow is acknowledge
 * first), and no order may be linked yet (a second one would strand the first).
 * Shared with the Work orders card, which shows the same reason on its prompt.
 */
export function workOrderCreateBlocker(
  machine: AlarmMachine,
  status: string | null | undefined,
  raised: { reference: string } | null
): string | null {
  if (!status) return 'AEM has not reported a status for this alarm yet.';
  if (!isKnownState(machine, status)) return `Helios cannot act on the status "${status}".`;
  if (status === 'CLOSED') return 'This alarm is closed.';
  if (raised) return `Work order ${raised.reference} is already linked to this alarm.`;
  if (status === 'ACTIVE_UNACK') return 'Acknowledge the alarm first.';
  if (isClearedState(status)) {
    return 'The condition has cleared; AEM takes no work order on a cleared alarm.';
  }
  if (!effectiveEvents(machine, status).includes('WO_CREATE')) {
    return 'Creating a work order is not available in this state.';
  }
  return null;
}

export function offersWorkOrderCreate(
  machine: AlarmMachine,
  status: string | null | undefined,
  hasRaisedOrder: boolean
): boolean {
  return (
    workOrderCreateBlocker(machine, status, hasRaisedOrder ? { reference: '' } : null) === null
  );
}

/** The operator actions always on show; a blocked one is disabled with its reason. */
const ACTIONS: OperatorEvent[] = ['ACK', 'CLOSE'];
const FINDING_ACTIONS: OperatorEvent[] = ['ACK'];

/** Chip tone for an alarm lifecycle state. */
export function stateTone(status: string | null | undefined): 'fault' | 'helios' | 'ok' | 'idle' {
  if (status === 'ACTIVE_UNACK') return 'fault';
  if (status === 'ACTIVE_ACK') return 'helios';
  if (isClearedState(status)) return 'ok';
  return 'idle';
}

/**
 * Events this card can render, in the order they should appear. Anything the
 * delta allows but this list omits is deliberately deferred, not forgotten.
 */
// WO_REOPEN is deliberately absent: reopening is hidden in GA-10 (Oct 26).
// CLOSE comes last: the closing step, available while the alarm is not closed.
const SUPPORTED: OperatorEvent[] = ['ACK', 'WO_CREATE', 'WO_COMPLETE', 'FORCE_CLEAR', 'CLOSE'];

const IDLE_LABELS: Record<OperatorEvent, string> = {
  ACK: 'Acknowledge',
  SHELVE: 'Shelve',
  UNSHELVE: 'Unshelve',
  WO_CREATE: 'Create work order',
  WO_COMPLETE: 'Mark work order complete',
  WO_REOPEN: 'Reopen work order',
  FORCE_CLEAR: 'Force clear',
  CLOSE: 'Close',
};

const PENDING_LABELS: Record<OperatorEvent, string> = {
  ACK: 'Acknowledging…',
  SHELVE: 'Shelving…',
  UNSHELVE: 'Unshelving…',
  WO_CREATE: 'Creating…',
  WO_COMPLETE: 'Marking complete…',
  WO_REOPEN: 'Reopening…',
  FORCE_CLEAR: 'Clearing…',
  CLOSE: 'Closing…',
};

/**
 * The open_loop happy path as the lifecycle tracker draws it: the alarm's two
 * states, then the work order's own statuses in the order the WOMS moves them.
 */
const LIFECYCLE = ['Raised', 'Ack', 'Scheduled', 'In progress', 'Completed'];
const FIRST_WO_STEP = 2;
const LAST_STEP = LIFECYCLE.length - 1;

type StepState = 'done' | 'current' | 'upcoming' | 'skipped';
export interface LifecycleStep {
  label: string;
  state: StepState;
}

/** The work-order step a work order's own status puts the tracker on. */
function workOrderStep(workOrder: Pick<WorkOrder, 'status'> | null): number {
  return workOrder?.status === 'completed'
    ? LAST_STEP
    : workOrder?.status === 'in_progress' || workOrder?.status === 'on_hold'
      ? FIRST_WO_STEP + 1
      : FIRST_WO_STEP;
}

/**
 * Where the alarm sits on {@link LIFECYCLE}, or null when its state is off the
 * path (cleared, shelved) and a tracker would only mislead. On the work-order
 * steps the current label is the order's real `status_label`, so "On hold",
 * "Canceled" or "Update overdue" read as the WOMS reports them.
 */
export function lifecycleSteps(
  alarm: Pick<Alarm, 'status' | 'acknowledgedBy' | 'acknowledgedAt'>,
  workOrder: Pick<WorkOrder, 'status' | 'status_label'> | null
): LifecycleStep[] | null {
  let at: number;
  switch (alarm.status) {
    case 'ACTIVE_UNACK':
      at = 0;
      break;
    case 'ACTIVE_ACK':
      // After helios-aem #134 an open work order is no longer an alarm state, so
      // an acknowledged alarm with a work order sits on the work-order steps.
      at = workOrder ? workOrderStep(workOrder) : 1;
      break;
    case 'WO_CREATED':
      at = workOrderStep(workOrder);
      break;
    case 'WO_COMPLETED':
      at = LAST_STEP;
      break;
    default:
      return null;
  }
  return LIFECYCLE.map((label, i) => {
    if (i === at) {
      const real = i >= FIRST_WO_STEP && i < LAST_STEP && workOrder?.status_label;
      return { label: real || label, state: 'current' };
    }
    if (i > at) return { label, state: 'upcoming' };
    // open_loop lets WO_CREATE fire straight from ACTIVE_UNACK, so a work order
    // state alone doesn't mean anyone acknowledged the alarm.
    if (i === 1 && !alarm.acknowledgedBy && !alarm.acknowledgedAt)
      return { label, state: 'skipped' };
    return { label, state: 'done' };
  });
}

type WorkOrderRef = { id: string; reference: string };

interface ActionFailure {
  event: OperatorEvent;
  /** 'workOrder': the WOMS write failed, alarm untouched. 'alarm': the AEM event failed. */
  step: 'workOrder' | 'alarm';
  error: Error;
  /** The work order the action is about; on an 'alarm' failure its write succeeded. */
  workOrder?: WorkOrderRef;
  /** Restored from the store's owed-step flag on mount, not reported by a request this card sent. */
  hydrated?: boolean;
}

const WORK_ORDER_DONE: Partial<Record<OperatorEvent, string>> = {
  WO_CREATE: 'was created',
  WO_COMPLETE: 'was marked complete',
};

/** Names both systems and the step that failed, so the operator knows what is real. */
function failureMessage({ event, step, error, workOrder }: ActionFailure, status?: string | null) {
  if (step === 'workOrder' && workOrder) {
    return `Work order ${workOrder.reference} could not be updated: ${error.message}. The alarm was not changed.`;
  }
  const done = WORK_ORDER_DONE[event];
  // The reason the alarm step failed (alarmEventErrorMessage) is kept, not
  // replaced by generic retry instructions: a 404/409/422 means retrying
  // alone won't help, and the operator needs to know why, not just that the
  // work order's own write already succeeded.
  if (workOrder && done) {
    return `Work order ${workOrder.reference} ${done}, but the alarm state could not be changed. ${alarmEventErrorMessage(error, event, status)}`;
  }
  return alarmEventErrorMessage(error, event, status);
}

const AlarmNextStepCard = forwardRef<AlarmNextStepCardHandle, Props>(function AlarmNextStepCard(
  { alarm, siteId, onEventApplied, machine = 'open_loop', finding = false },
  ref
) {
  const [raiseOpen, setRaiseOpen] = useState(false);
  const [pending, setPending] = useState<OperatorEvent | null>(null);
  // Same guard as the card's own buttons: nothing opens while an action is in
  // flight, so an order cannot be raised against an alarm that is closing.
  useImperativeHandle(
    ref,
    () => ({
      raiseWorkOrder: () => {
        if (pending === null) setRaiseOpen(true);
      },
    }),
    [pending]
  );
  // Which step of the last action failed. Explicit, so a two-system action
  // (WOMS then AEM) can say exactly which write went through and retry only
  // the one that did not. Cleared when a new action starts.
  const [failure, setFailure] = useState<ActionFailure | null>(null);
  // AEM accepted the event but the re-read alarm had not moved.
  const [noop, setNoop] = useState<OperatorEvent | null>(null);
  // AEM accepted the event but the alarm could not be re-read afterwards.
  const [rereadFailed, setRereadFailed] = useState<OperatorEvent | null>(null);
  const latestStatus = useRef(alarm.status);
  latestStatus.current = alarm.status;

  const isMounted = useIsMounted();
  const eventMutation = useApplyAlarmEvent();

  const alarmId = alarm.alarmId;
  const ownerEnabled = useFlagOn('alarmOwnerEnabled');
  const raisedLink = useRaisedWorkOrder(alarm);
  // The order's own status, read from the WOMS, so the tracker shows where the
  // provider is with it rather than only that it exists. Also gives the human
  // reference when the link came from AEM, which stores only the order's id.
  const { workOrder } = useWorkOrderLookup(raisedLink?.siteId ?? null, raisedLink?.id ?? null);
  const raisedWorkOrder =
    raisedLink && workOrder?.reference && raisedLink.reference === raisedLink.id
      ? { ...raisedLink, reference: workOrder.reference }
      : raisedLink;
  const rememberWorkOrder = useAlarmWorkOrderStore(s => s.remember);
  const setPendingEvent = useAlarmWorkOrderStore(s => s.setPendingEvent);
  const storedPendingEvent = usePendingWorkOrderEvent(alarmId);
  // AEM carrying `work_order_id` means it runs the single lifecycle (helios-aem
  // #134), where WO_CREATE is a retired event applied as a no-op ACK: nothing
  // is owed once the order is linked, so a leftover WO_CREATE flag (from a card
  // that unmounted before AEM answered) is stale. The FSM-based check below
  // cannot see that: the UI's open_loop table still offers WO_CREATE on an
  // ACTIVE_ACK alarm, so the flag would otherwise never trip as stale.
  const rememberedPendingEvent =
    storedPendingEvent === 'WO_CREATE' && alarm.workOrderId ? null : storedPendingEvent;

  // The machine lets an unacknowledged alarm go straight to WO_CREATE, but the
  // operator flow is acknowledge first: one next step at a time.
  const rawAvailable = effectiveEvents(machine, alarm.status).filter(
    e => SUPPORTED.includes(e) && (!finding || e === 'ACK')
  );
  // Whether the remembered pending step is still owed, per the FSM itself: once
  // it actually lands, the alarm's state moves and the FSM stops offering it —
  // the same signal used elsewhere to say an action is no longer available. A
  // flag this disagrees with is stale (the event landed while this card, or the
  // whole app, was gone) rather than a real outstanding retry.
  const pendingEvent =
    rememberedPendingEvent && rawAvailable.includes(rememberedPendingEvent as OperatorEvent)
      ? (rememberedPendingEvent as OperatorEvent)
      : null;
  useEffect(() => {
    if (storedPendingEvent && !pendingEvent && alarmId) setPendingEvent(alarmId, undefined);
  }, [alarmId, storedPendingEvent, pendingEvent, setPendingEvent]);
  // Hydrates the failure/retry UI across a remount (navigating away and back)
  // while a two-system action is still owed: without this, the operator would
  // see a plain "Create work order"/"Mark complete" button again and could
  // orphan the work order whose write already landed (see useWorkOrderMutation
  // and alarmWorkOrderStore.pendingEvent).
  useEffect(() => {
    // Not while this card's own event is in flight: sendEvent sets the flag
    // before AEM answers, and its callbacks report the outcome. Hydrating
    // here said the update "did not complete" for one that had not finished.
    if (pending !== null) return;
    if (!pendingEvent || !raisedWorkOrder) {
      // Nothing is owed any more (AEM accepted the event after a remount, or
      // the flag went stale), so a banner restored from the flag is wrong.
      setFailure(prev => (prev?.hydrated ? null : prev));
      return;
    }
    setFailure(
      prev =>
        prev ?? {
          event: pendingEvent,
          step: 'alarm',
          error: new Error('The alarm update did not complete.'),
          workOrder: raisedWorkOrder,
          hydrated: true,
        }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alarmId, pendingEvent, pending]);
  // A status AEM didn't report, or one this machine doesn't know, resets to q0
  // for the lookup above (CLEARED_ACK for open_loop), which now has a CLOSE cell.
  // Closing an alarm whose state we can't read is not something to offer.
  const stateReadable = !!alarm.status && isKnownState(machine, alarm.status);
  const available = rawAvailable.filter(
    e =>
      !(e === 'WO_CREATE' && !offersWorkOrderCreate(machine, alarm.status, !!raisedWorkOrder)) &&
      !(e === 'CLOSE' && !stateReadable) &&
      e !== pendingEvent
  );
  // A reset lands open_loop on CLEARED_ACK, so AEM can hold a cleared alarm as
  // acknowledged when nobody did; the rail reads that as "Ack · skipped". No
  // Acknowledge is offered there: ACK is a self-loop, and AEM writes nothing
  // for an event that leaves the state unchanged (confirmed on dev, Oct 28:
  // 204, re-read unchanged), so the button would do nothing. Needs AEM to
  // record the acknowledger on that ACK, or to stop resetting to CLEARED_ACK.
  // AEM has not reported a status (null on records stored before it set one).
  // The machine's reset rule would treat that as q0, which for open_loop is
  // CLEARED_ACK: saying "no further action" there would read as if the alarm
  // were resolved, when it is open and merely unreported. Only a missing status
  // counts: a reported value this machine doesn't recognise (the mapper keeps
  // it as-is) was reported, so it must not be called unreported. It resets to
  // q0 the same way, though, so "no further action" would read as resolved for
  // it too: say Helios doesn't recognise the state instead.
  const statusUnreported = !alarm.status;
  const statusUnrecognised = !statusUnreported && !isKnownState(machine, alarm.status);

  // Closing while the condition is still active can reopen as a new alarm (AEM
  // ADR-0042), so the Close action says so; a cleared alarm closes cleanly.
  const isActiveAlarm = alarm.status === 'ACTIVE_UNACK' || alarm.status === 'ACTIVE_ACK';
  /** Why an action the card always shows cannot run right now. */
  function blockedReason(event: OperatorEvent | 'REOPEN'): string {
    if (event === 'REOPEN') {
      return 'AEM has no reopen: a closed alarm that fires again opens a new alarm.';
    }
    if (event === 'WO_CREATE') {
      return (
        workOrderCreateBlocker(machine, alarm.status, raisedWorkOrder) ??
        'Creating a work order is not available right now.'
      );
    }
    if (statusUnreported) return 'AEM has not reported a status for this alarm yet.';
    if (statusUnrecognised) return `Helios cannot act on the status "${alarm.status}".`;
    const closed = alarm.status === 'CLOSED';
    if (event === 'ACK') {
      if (closed) return 'This alarm is closed.';
      if (alarm.acknowledgedBy || alarm.acknowledgedAt) {
        return `Already acknowledged${alarm.acknowledgedBy ? ` by ${alarm.acknowledgedBy}` : ''}.`;
      }
      if (isAckedState(alarm.status)) {
        return 'AEM holds this alarm as acknowledged, so acknowledging changes nothing. Close it; if the condition returns it opens as a new alarm.';
      }
      return 'Acknowledging is not available in this state.';
    }
    if (event === 'CLOSE') {
      return closed ? 'This alarm is already closed.' : 'Closing is not available in this state.';
    }
    return 'Not available in this state.';
  }

  // The tracker is the alarm lifecycle, with work-order steps a finding never has.
  const steps = finding ? null : lifecycleSteps(alarm, workOrder);
  const buttons: OperatorEvent[] = available;

  // A successful event stays pending until the re-read alarm moves, or at the
  // latest until the re-read settles. Clearing on the 204 re-enabled the OLD
  // state's buttons for the length of the refetch, so "Create work order" could
  // be pressed twice. Settling is the fallback for a re-read that fails or
  // comes back unchanged: the buttons then show the state the alarm really has,
  // and the operator can act again.
  useEffect(() => {
    setPending(null);
    setNoop(null);
  }, [alarm.status]);
  // The badge follows the work order once one exists, like the header chip.
  const status = shownStatusLabel(alarm.status, workOrder);

  /**
   * Re-read the alarm after AEM accepted an event. A 204 only says the event
   * was valid: AEM writes nothing for one that leaves the state unchanged (seen
   * on dev, Oct 28: ACK on CLEARED_ACK), and a re-read can fail. Both used to
   * pass as success; they are reported instead, so nothing fails silently.
   */
  function confirm(event?: OperatorEvent) {
    const from = alarm.status;
    const reread = onEventApplied();
    if (!reread) return;
    Promise.resolve(reread).then(
      result => {
        const read = alarmOf(result);
        const after = read ? read.status : latestStatus.current;
        // Only ACK and CLOSE move an alarm; AEM applies WO_CREATE as ACK, so the
        // state is not supposed to change on it.
        if (
          (event === 'ACK' || event === 'CLOSE') &&
          after === from &&
          effectiveEvents(machine, from).includes(event)
        ) {
          setNoop(event);
        }
        setPending(null);
      },
      () => {
        setRereadFailed(event ?? null);
        setPending(null);
      }
    );
  }

  // One operation at a time: a new one drops the previous one's error so the
  // card never reports a stale failure against a fresh attempt.
  function begin(op: OperatorEvent) {
    setFailure(null);
    setNoop(null);
    setRereadFailed(null);
    eventMutation.reset();
    setPending(op);
  }

  /** The alarm step. `workOrder` is the WOMS write that already succeeded, if any. */
  function sendEvent(event: OperatorEvent, workOrder?: WorkOrderRef) {
    if (!alarmId) return;
    begin(event);
    // Durable (survives this card unmounting before AEM answers): a two-system
    // action's alarm step is remembered as owed the moment it is sent, not
    // only in this render's local `failure` state, so a remount can restore
    // the retry instead of re-offering the action that already wrote the
    // work order (see the hydration effect above).
    if (workOrder) setPendingEvent(alarmId, event);
    eventMutation.mutate(
      { siteId, alarmId, event, settlesPending: !!workOrder },
      {
        onSuccess: () => {
          if (workOrder) setPendingEvent(alarmId, undefined);
          confirm(event);
        },
        onError: error => {
          setPending(null);
          setFailure({ event, step: 'alarm', error, workOrder });
        },
      }
    );
  }

  /**
   * Runs from the create mutation's hook-level onSuccess, so it still fires if
   * the modal (or this card) has unmounted by the time the WOMS answers. The
   * link is remembered before the event goes out: nothing upstream records it,
   * and losing it strands the work order (see useAlarmWorkOrderLinks).
   */
  function onWorkOrderCreated(workOrder: WorkOrder) {
    // Presentation only: closing this card's own modal state means nothing
    // once the card that owns it is gone. The link and the alarm event below
    // are durable and must run either way.
    if (isMounted.current) setRaiseOpen(false);
    if (!alarmId) return;
    const ref = { id: workOrder.id, reference: workOrder.reference, siteId };
    rememberWorkOrder(alarmId, ref, { path: alarmDetailPath(alarm), label: alarmLinkLabel(alarm) });
    sendEvent('WO_CREATE', ref);
  }

  // A two-system action reports which step failed; Start work is a WOMS-only
  // write, so its error is the mutation's own.
  const failureText = failure ? failureMessage(failure, alarm.status) : null;
  // Only an alarm step that followed a successful WOMS write is retried on its
  // own, and only when the failure itself is retryable — a 404/409/422 means
  // the alarm's state (or the request) is the problem, not the attempt, so
  // offering Retry there would just repeat a response already rejected.
  const retryAlarmStep =
    failure?.step === 'alarm' && failure.workOrder && isAlarmEventRetryable(failure.error)
      ? failure
      : null;

  return (
    <section className="ald-card ald-next-step">
      <div className="ald-ns-head">
        <div className="ald-card-title">Next step</div>
        {status && (
          <span className={`ald-ns-state ald-ns-state--${stateTone(alarm.status)}`}>{status}</span>
        )}
      </div>

      {ownerEnabled && alarmId && (
        <AlarmOwnerRow
          siteId={siteId}
          alarmId={alarmId}
          owner={alarm.owner}
          assignedAt={alarm.assignedAt}
          closed={alarm.status === 'CLOSED'}
          onAssigned={onEventApplied}
        />
      )}

      {/* The open_loop happy path, so the operator sees where the alarm sits in
          the machine rather than only the buttons it currently accepts. */}
      {steps && (
        <ol className="ald-ns-steps" aria-label="Alarm lifecycle">
          {steps.map((step, i) => (
            <li
              key={LIFECYCLE[i]}
              className={`ald-ns-step ald-ns-step--${step.state}`}
              aria-current={step.state === 'current' ? 'step' : undefined}
            >
              {/* The ends are icons alone, the bell every alarm starts at and
                  the tick it ends on; their labels are for screen readers. */}
              <span className="ald-ns-dot" aria-hidden="true">
                {i === 0 ? (
                  <Notification size={10} />
                ) : i === LAST_STEP || step.state === 'done' ? (
                  <Checkmark size={10} />
                ) : null}
              </span>
              <span
                className={
                  i === 0 || i === LAST_STEP ? 'cds--visually-hidden' : 'ald-ns-step-label'
                }
              >
                {step.label}
                {step.state === 'skipped' && <span className="ald-ns-skipped"> · skipped</span>}
              </span>
            </li>
          ))}
        </ol>
      )}

      {!alarmId && (
        <p className="ald-actionbar-note">
          This alarm can&apos;t be identified, so it can&apos;t be acted on from here.
        </p>
      )}

      {alarmId && (
        <div className="ald-ns-actions">
          {(finding ? FINDING_ACTIONS : ACTIONS).map(event => {
            const offered = buttons.includes(event);
            return (
              <Button
                key={event}
                kind={event === 'ACK' ? 'primary' : 'tertiary'}
                size="md"
                disabled={!offered || pending !== null}
                title={offered ? undefined : blockedReason(event)}
                onClick={() => sendEvent(event)}
              >
                {pending === event ? PENDING_LABELS[event] : IDLE_LABELS[event]}
              </Button>
            );
          })}
          {alarm.status === 'CLOSED' && (
            <Button kind="tertiary" size="md" disabled title={blockedReason('REOPEN')}>
              Reopen
            </Button>
          )}
        </div>
      )}

      {alarmId && buttons.includes('CLOSE') && isActiveAlarm && (
        <p className="ald-actionbar-note">
          The condition is still active. If it keeps firing, this alarm may come back as a new one,
          without its assignee, work order or acknowledgement.
        </p>
      )}

      {alarmId && buttons.length === 0 && !finding && (
        <p className="ald-actionbar-note">
          {statusUnreported
            ? 'This alarm has no status yet, so no actions are available. It is still open.'
            : statusUnrecognised
              ? `This alarm's status ("${alarm.status}") is not one Helios can act on.`
              : 'No further action is available for this alarm.'}
        </p>
      )}

      {noop && (
        <p className="ald-ns-warning" role="alert">
          AEM accepted {IDLE_LABELS[noop].toLowerCase()} but the alarm still reads{' '}
          {status ? `"${status}"` : 'the same'}. Nothing changed: try again, and tell the alarms
          team if it keeps happening.
        </p>
      )}
      {rereadFailed && (
        <p className="ald-ns-warning" role="alert">
          {IDLE_LABELS[rereadFailed]} was accepted, but the alarm could not be re-read. Reload to
          see its current state.
        </p>
      )}
      {failureText && (
        <p className="ald-wo-cmms-error" role="alert">
          {failureText}
        </p>
      )}

      {retryAlarmStep && (
        <Button
          kind="tertiary"
          size="sm"
          disabled={pending !== null}
          onClick={() => sendEvent(retryAlarmStep.event, retryAlarmStep.workOrder)}
        >
          Retry alarm update
        </Button>
      )}

      {raiseOpen && (
        <WorkOrderCreateModal
          open
          siteId={siteId}
          onClose={() => setRaiseOpen(false)}
          onCreated={onWorkOrderCreated}
          initial={workOrderDraftFromAlarm(alarm)}
        />
      )}
    </section>
  );
});

export default AlarmNextStepCard;
